import React, { useState, useEffect, useContext } from 'react';
import { Button, Card, Tabs, Row, Col, Image as AntImage, Typography } from 'antd'; // Added Typography
import { LeftOutlined, RightOutlined, PlayCircleOutlined, ReadOutlined, PlaySquareOutlined } from '@ant-design/icons'; // Added more icons
import AnimatedSection from './AnimatedSection';
import { Context } from '../store/Context';

const { TabPane } = Tabs;
const { Title } = Typography; // Use Ant Typography Title

// Accept backgroundUrl prop
const NewsGallery = ({ backgroundUrl }) => {
  const { state } = useContext(Context);
  // Remove newsGalleryBackground from state destructuring if it exists there
  const { newsItems = [] } = state;

  const articles = newsItems.filter((item) => item.type === 'article');
  const videos = newsItems.filter((item) => item.type === 'video');

  const [activeArticleIndex, setActiveArticleIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(3);

  useEffect(() => {
    const updateItems = () => {
      const width = window.innerWidth;
      if (width < 640) setItemsToShow(1);
      else if (width < 1024) setItemsToShow(2);
      else setItemsToShow(3);
      setActiveArticleIndex(0);
      setActiveVideoIndex(0);
    };
    updateItems();
    window.addEventListener('resize', updateItems);
    return () => window.removeEventListener('resize', updateItems);
  }, []);

   const handlePrev = (type) => {
    if (type === 'article') {
      setActiveArticleIndex((prev) => (prev === 0 ? Math.max(0, articles.length - itemsToShow) : prev - 1));
    } else {
      setActiveVideoIndex((prev) => (prev === 0 ? Math.max(0, videos.length - itemsToShow) : prev - 1));
    }
  };

  const handleNext = (type) => {
    if (type === 'article') {
      setActiveArticleIndex((prev) => (prev >= articles.length - itemsToShow ? 0 : prev + 1));
    } else {
      setActiveVideoIndex((prev) => (prev >= videos.length - itemsToShow ? 0 : prev + 1));
    }
  };


  const renderItems = (items, activeIndex) => {
    const visibleItems = items.slice(activeIndex, activeIndex + itemsToShow);
     while (visibleItems.length > 0 && visibleItems.length < itemsToShow) {
        visibleItems.push({ id: `placeholder-${visibleItems.length}`, type: 'placeholder' });
     }

    return visibleItems.map((item, index) => (
      <Col key={item.id} xs={24} sm={12} lg={8} style={{ display: 'flex' }}> {/* Use flex for equal height */}
         {item.type === 'placeholder' ? (
             <div style={{ flexGrow: 1 }}></div> // Empty filling div
         ) : item.type === 'article' ? (
          <AnimatedSection style={{ transitionDelay: `${index * 100}ms`, width: '100%' }}>
            <Card
              hoverable
              className="news-gallery-card article-card" // Add specific class
              style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }} // Ensure full height
              cover={
                <div className="news-gallery-cover">
                  <AntImage alt={item.title} src={item.image} preview={false} />
                </div>
              }
              actions={[ // Use actions for buttons
                  <Button
                     type="primary" // Make it look like a button
                     icon={<ReadOutlined />}
                     href={item.link || '#'}
                     target="_blank"
                     rel="noopener noreferrer"
                     className="news-action-button"
                   >
                     Read More
                  </Button>
              ]}
            >
              <div className="news-date">{item.date}</div>
              <Title level={5} className="news-title">{item.title}</Title> {/* Use Ant Title */}
              <p className="news-excerpt">{item.excerpt}</p>
            </Card>
          </AnimatedSection>
        ) : ( // Video
          <AnimatedSection style={{ transitionDelay: `${index * 100}ms`, width: '100%' }}>
            <Card
                hoverable
                className="news-gallery-card video-card" // Add specific class
                style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
                cover={
                  <div className="video-card-cover" onClick={() => window.open(item.videoUrl || '#', '_blank')}>
                    <AntImage alt={item.title} src={item.thumbnail} preview={false} />
                    <div className="video-card-cover-overlay">
                      <div className="video-play-button"><PlayCircleOutlined /></div>
                    </div>
                    <div className="video-duration">{item.duration}</div>
                  </div>
                 }
                 actions={[
                     <Button
                         type="primary"
                         icon={<PlaySquareOutlined />}
                         href={item.videoUrl || '#'}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="news-action-button"
                     >
                         Watch Now
                     </Button>
                 ]}
            >
              <Title level={5} className="video-title">{item.title}</Title>
              {/* Optional: Add short description here if needed */}
            </Card>
          </AnimatedSection>
        )}
      </Col>
    ));
  };


  return (
     // Apply background to the main section tag
    <section
        className="section-with-bg news-gallery-section"
        style={{ backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none' }}
    >
        <div className="section-bg-overlay"></div>
      <div className="section-content-wrapper container news-gallery-content"> {/* Keep container */}
        <AnimatedSection className="w-full"> {/* Animate the whole content block */}
          <h2 className="section-title"> {/* Styles via CSS */}
            Latest Updates
          </h2>

          <Tabs defaultActiveKey="articles" className="news-gallery-tabs">
             <div className="tabs-header-controls"> {/* Wrapper for TabsList and Controls */}
                <Tabs.TabList>
                    <Tabs.Tab key="articles" tab="Articles" />
                    <Tabs.Tab key="videos" tab="Videos" />
                 </Tabs.TabList>
                 {/* Controls will be positioned via CSS relative to this wrapper */}
             </div>

            <TabPane tab="Articles" key="articles">
                <div className="news-gallery-tab-controls">
                    <Button icon={<LeftOutlined />} onClick={() => handlePrev('article')} disabled={articles.length <= itemsToShow}/>
                    <Button icon={<RightOutlined />} onClick={() => handleNext('article')} disabled={articles.length <= itemsToShow} />
                </div>
              <Row gutter={[24, 24]} justify="center" className="news-gallery-grid">
                 {renderItems(articles, activeArticleIndex)}
              </Row>
            </TabPane>
            <TabPane tab="Videos" key="videos">
                 <div className="news-gallery-tab-controls">
                    <Button icon={<LeftOutlined />} onClick={() => handlePrev('video')} disabled={videos.length <= itemsToShow} />
                    <Button icon={<RightOutlined />} onClick={() => handleNext('video')} disabled={videos.length <= itemsToShow} />
                </div>
              <Row gutter={[24, 24]} justify="center" className="news-gallery-grid">
                 {renderItems(videos, activeVideoIndex)}
              </Row>
            </TabPane>
          </Tabs>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default NewsGallery;